import { setPriceText } from './dom-utils.js';

/**
 * Product Page JavaScript Handler
 *
 * Manages quantity-based pricing for coffee products without selling plans.
 * Updates quantity button labels (Box/Bag) and CTA button pricing in real-time.
 *
 * Note: Skio handles subscription products with selling plan groups.
 */

(function () {
  'use strict';

  const ProductPage = {
    config: {
      selectors: {
        quantityButtonActive: '.quantity-button--active',
        btnPrice: '.btn-price',
        btnCompareAtPrice: '.btn-compare-at-price',
        addToCartButton: '[data-add-to-cart]',
        quantityButtons: '.quantity-button',
        productSection: '[data-section-type="product"]'
      },
      events: {
        variantChange: 'variant:change',
        quantityUpdated: 'quantity:updated'
      }
    },

    currentVariant: null,
    currentQuantity: 1,
    isOnSale: false,
    initialCompareAtPrice: null,

    init() {
      this.bindEvents();
      this.initializeFromDOM();
    },

    initializeFromDOM() {
      const activeQuantityButton = document.querySelector(
        this.config.selectors.quantityButtonActive
      );
      if (activeQuantityButton) {
        this.currentQuantity =
          parseInt(activeQuantityButton.dataset.value) || 1;
      }

      // Check if product has sale enabled
      const productSection = document.querySelector(
        this.config.selectors.productSection
      );
      if (productSection) {
        this.isOnSale = productSection.dataset.isOnSale === 'true';
      }

      const btnCompareAtPrice = document.querySelector(
        this.config.selectors.btnCompareAtPrice
      );
      if (btnCompareAtPrice) {
        const raw = btnCompareAtPrice.getAttribute('data-compare-at-price');
        if (raw) {
          this.initialCompareAtPrice = parseFloat(raw);
        }
      }

      this.updatePricingDisplay();
    },

    bindEvents() {
      document.addEventListener(
        this.config.events.variantChange,
        this.handleVariantChange.bind(this)
      );
      document.addEventListener(
        this.config.events.quantityUpdated,
        this.handleQuantityChange.bind(this)
      );
    },

    handleVariantChange(event) {
      const { variant } = event.detail;
      if (!variant) return;

      this.currentVariant = variant;

      // Update current quantity from active quantity button
      const activeQuantityButton = document.querySelector(
        this.config.selectors.quantityButtonActive
      );
      if (activeQuantityButton) {
        this.currentQuantity =
          parseInt(activeQuantityButton.dataset.value) || 1;
      }

      this.updateQuantityButtonLabels(variant);
      this.updatePricingDisplay();
    },

    handleQuantityChange(event) {
      const { quantity } = event.detail || {};
      if (quantity) {
        this.currentQuantity = parseInt(quantity);
      }
      this.updatePricingDisplay();
    },

    updatePricingDisplay() {
      const btnPrice = document.querySelector(this.config.selectors.btnPrice);
      const btnCompareAtPrice = document.querySelector(
        this.config.selectors.btnCompareAtPrice
      );

      if (!btnPrice) return;

      // Check if this is a subscription-widget-managed element
      const isStayaiManaged = btnPrice.hasAttribute('pdpAtcBtn-price');

      // Only skip if Stayai-managed AND we don't have a current variant
      // (i.e., let Stayai handle initial load, but we handle variant changes)
      if (isStayaiManaged && !this.currentVariant) return;

      const addToCartButton = document.querySelector(
        this.config.selectors.addToCartButton
      );

      // Determine if we're getting price from variant object or data attribute
      let variantPrice;
      let priceIsAlreadyDiscounted = false;

      if (this.currentVariant) {
        variantPrice = this.currentVariant.price;
      } else {
        const dataPrice = addToCartButton?.getAttribute('data-variant-price');
        variantPrice = dataPrice ? parseFloat(dataPrice) : null;
        priceIsAlreadyDiscounted = true;
      }

      if (!variantPrice) return;

      const isSaleActive = this.isOnSale && window.isSaleEnabled;
      let displayPrice = variantPrice;
      let compareAtPrice = null;

      if (this.currentVariant?.compare_at_price) {
        compareAtPrice = this.currentVariant.compare_at_price;
      } else if (this.initialCompareAtPrice) {
        compareAtPrice = this.initialCompareAtPrice;
      }

      if (isSaleActive && window.saleDiscountPercentage) {
        const percentageOff = window.saleDiscountPercentage / 100;
        const multiplier = 1 - percentageOff;

        if (!priceIsAlreadyDiscounted) {
          displayPrice = Math.round(variantPrice * multiplier);
          if (!compareAtPrice) compareAtPrice = variantPrice;
        } else {
          const originalPrice = Math.round(variantPrice / multiplier);
          displayPrice = variantPrice;
          if (!compareAtPrice) compareAtPrice = originalPrice;
        }
      }

      // Calculate and update total price (main price)
      const totalPrice = displayPrice * this.currentQuantity;
      const formattedPrice = theme.Currency.formatMoney(
        totalPrice,
        theme.settings.moneyFormat
      );
      setPriceText(btnPrice, formattedPrice);

      // Handle compare-at-price (strikethrough)
      if (btnCompareAtPrice) {
        if (compareAtPrice && compareAtPrice > displayPrice) {
          const totalComparePrice = compareAtPrice * this.currentQuantity;
          setPriceText(
            btnCompareAtPrice,
            theme.Currency.formatMoney(
              totalComparePrice,
              theme.settings.moneyFormat
            )
          );
          btnCompareAtPrice.style.display = '';
        } else {
          btnCompareAtPrice.style.display = 'none';
        }
      }
    },

    updateQuantityButtonLabels(variant) {
      const quantityButtons = document.querySelectorAll(
        this.config.selectors.quantityButtons
      );

      const variantTitle = variant.title.toLowerCase();
      let packageType = 'bag'; // default

      // Determine package type based on variant title
      if (variantTitle.includes('pods')) {
        packageType = 'box';
      } else if (
        variantTitle.includes('pack') ||
        variantTitle.includes('cans')
      ) {
        packageType = 'pack';
      }

      for (const quantityButton of quantityButtons) {
        const count = Number(quantityButton?.dataset?.value || 1);
        const isPlural = count > 1;
        const packageTypeSpan = quantityButton.querySelector('.package-type');

        if (packageTypeSpan) {
          let labelText = '';

          switch (packageType) {
            case 'box':
              labelText = isPlural ? 'Boxes' : 'Box';
              break;
            case 'pack':
              labelText = isPlural ? 'Cases' : 'Case';
              break;
            case 'bag':
            default:
              labelText = isPlural ? 'Bags' : 'Bag';
              break;
          }

          packageTypeSpan.innerText = labelText;
        }
      }
    }
  };

  ProductPage.init();
})();
